import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  SubgroupTemplateSet,
  useCreateSubgroupTemplate,
  useDeleteSubgroupTemplate,
  useInstantiateSubgroupTemplate,
  useSubgroupTemplates,
  useUpdateSubgroupTemplate,
  type InstantiateTemplateResult,
  type SubgroupTemplateEntry
} from '../../api/useSubgroups';

/**
 * Small admin for reusable subgroup template sets: list / create / edit the
 * sets, plus a one-click "Instantiate into this group" action (upsert-by-name;
 * creates the EMPTY subgroups, the operator then assigns pairs). The editor
 * captures each template subgroup's name + color; their `config` snapshot
 * defaults to empty so instantiated subgroups start blank — the operator tunes
 * each subgroup's config in the group via the config dialog.
 */

interface Props {
  open: boolean;
  /** Active group the "Instantiate" action targets (null = no group selected). */
  groupId: string | null;
  onClose: () => void;
}

interface EntryDraft {
  name: string;
  color: string;
  /** Preserved opaque so editing a set's identity never drops its config. */
  config: SubgroupTemplateEntry['config'];
}

const toDrafts = (set: SubgroupTemplateSet | null): EntryDraft[] =>
  (set?.subgroups ?? []).map(s => ({
    name: s.name,
    color: s.color ?? '',
    config: s.config ?? {}
  }));

export function SubgroupTemplatesDialog({ open, groupId, onClose }: Props) {
  const { data: templates } = useSubgroupTemplates(open);
  const createMutation = useCreateSubgroupTemplate();
  const updateMutation = useUpdateSubgroupTemplate();
  const deleteMutation = useDeleteSubgroupTemplate();
  const instantiateMutation = useInstantiateSubgroupTemplate();

  const [selectedId, setSelectedId] = useState<string | 'new'>('new');
  const [setName, setSetName] = useState('');
  const [entries, setEntries] = useState<EntryDraft[]>([]);
  const [instantiateResult, setInstantiateResult] =
    useState<InstantiateTemplateResult | null>(null);

  const selectedTemplate = useMemo(
    () =>
      selectedId === 'new'
        ? null
        : (templates ?? []).find(t => t._id === selectedId) ?? null,
    [templates, selectedId]
  );

  useEffect(() => {
    if (!open) return;
    setSetName(selectedTemplate?.name ?? '');
    setEntries(toDrafts(selectedTemplate));
    setInstantiateResult(null);
  }, [open, selectedTemplate]);

  const busy =
    createMutation.isLoading ||
    updateMutation.isLoading ||
    deleteMutation.isLoading ||
    instantiateMutation.isLoading;

  const buildEntries = (): SubgroupTemplateEntry[] =>
    entries
      .filter(e => e.name.trim())
      .map((e, i) => ({
        name: e.name.trim(),
        color: e.color.trim() || undefined,
        sortOrder: i,
        config: e.config ?? {}
      }));

  const handleSave = async () => {
    const name = setName.trim();
    if (!name) return;
    const subgroups = buildEntries();
    try {
      if (selectedTemplate) {
        await updateMutation.mutateAsync({
          id: selectedTemplate._id,
          dto: { name, subgroups }
        });
      } else {
        const created = await createMutation.mutateAsync({ name, subgroups });
        setSelectedId(created._id);
      }
    } catch {
      /* surfaced by the backstop */
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;
    try {
      await deleteMutation.mutateAsync(selectedTemplate._id);
      setSelectedId('new');
    } catch {
      /* surfaced by the backstop */
    }
  };

  const handleInstantiate = async () => {
    if (!selectedTemplate || !groupId) return;
    setInstantiateResult(null);
    try {
      const result = await instantiateMutation.mutateAsync({
        groupId,
        templateId: selectedTemplate._id
      });
      setInstantiateResult(result);
    } catch {
      /* surfaced by the backstop */
    }
  };

  const updateEntry = (i: number, patch: Partial<EntryDraft>) =>
    setEntries(prev =>
      prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e))
    );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem' }}>Subgroup templates</DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <TextField
            select
            size="small"
            label="Template set"
            value={selectedId}
            onChange={e => setSelectedId(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="new">+ New template…</MenuItem>
            {(templates ?? []).map(t => (
              <MenuItem key={t._id} value={t._id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>
          {selectedTemplate && (
            <Button
              color="error"
              size="small"
              onClick={() => void handleDelete()}
              disabled={busy}
              sx={{ fontSize: '0.75rem' }}
            >
              Delete set
            </Button>
          )}
        </Stack>

        <TextField
          size="small"
          fullWidth
          label="Set name"
          value={setName}
          onChange={e => setSetName(e.target.value)}
          sx={{ mb: 1.5 }}
        />

        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.5 }}>
          Subgroups in this set
        </Typography>
        <Stack spacing={1}>
          {entries.map((entry, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                label="Name"
                value={entry.name}
                onChange={e => updateEntry(i, { name: e.target.value })}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Color"
                value={entry.color}
                onChange={e => updateEntry(i, { color: e.target.value })}
                placeholder="#42a5f5"
                sx={{ width: 110 }}
              />
              <IconButton
                size="small"
                onClick={() =>
                  setEntries(prev => prev.filter((_, idx) => idx !== i))
                }
                aria-label="remove subgroup from template"
              >
                <DeleteOutlineIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Stack>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() =>
              setEntries(prev => [...prev, { name: '', color: '', config: {} }])
            }
            sx={{ alignSelf: 'flex-start', fontSize: '0.75rem' }}
          >
            Add subgroup
          </Button>
        </Stack>

        {selectedTemplate && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Button
                variant="outlined"
                size="small"
                disabled={busy || !groupId}
                onClick={() => void handleInstantiate()}
                sx={{ fontSize: '0.75rem' }}
                title={
                  groupId
                    ? 'Create this set’s subgroups in the selected group (upsert by name).'
                    : 'Select a single group first.'
                }
              >
                {instantiateMutation.isLoading ? (
                  <CircularProgress size={13} />
                ) : (
                  'Instantiate into this group'
                )}
              </Button>
              {instantiateResult && (
                <Typography
                  sx={{ fontSize: '0.76rem', color: 'success.light' }}
                  role="status"
                >
                  {instantiateResult.created.length} created ·{' '}
                  {instantiateResult.skipped.length} skipped
                </Typography>
              )}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={busy || !setName.trim()}
        >
          {createMutation.isLoading || updateMutation.isLoading ? (
            <CircularProgress size={14} color="inherit" />
          ) : selectedTemplate ? (
            'Save set'
          ) : (
            'Create set'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
